import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, MinusCircle, Briefcase, MapPin, Calendar, ExternalLink, Loader2, Filter, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase/client';
import { withdrawJobApplication } from '../../lib/supabase/applications';
import type { JobApplication } from '../../types/applications';

interface ApplicationWithJob extends JobApplication {
  jobTitle?: string;
  jobLocation?: string;
  employerName?: string;
}

type StatusFilter = 'all' | 'pending' | 'accepted' | 'rejected' | 'withdrawn';

export default function MyApplications() {
  const { user } = useAuth();
  const [applications, setApplications] = useState<ApplicationWithJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [withdrawingId, setWithdrawingId] = useState<string | null>(null);

  useEffect(() => {
    if (user) loadApplications();
  }, [user]);

  const loadApplications = async () => {
    if (!user) return;
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('job_applications')
        .select(`
          *,
          jobs:job_id (
            title,
            location,
            farmer_name
          )
        `)
        .eq('applicant_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mapped: ApplicationWithJob[] = (data || []).map((row: any) => ({
        id: row.id,
        jobId: row.job_id,
        applicantId: row.applicant_id,
        message: row.message,
        status: row.status,
        resumeUrl: row.resume_url,
        voiceIntroUrl: row.voice_intro_url,
        videoUrl: row.video_url,
        notes: row.notes,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        applicantEmail: row.applicant_email,
        applicantPhone: row.applicant_phone,
        jobTitle: row.jobs?.title,
        jobLocation: row.jobs?.location,
        employerName: row.jobs?.farmer_name,
      }));

      setApplications(mapped);
    } catch (err) {
      console.error('Failed to load applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (applicationId: string) => {
    setWithdrawingId(applicationId);
    try {
      const { error } = await withdrawJobApplication(applicationId);
      if (error) throw error;
      setApplications((prev) =>
        prev.map((a) => (a.id === applicationId ? { ...a, status: 'withdrawn' as const } : a))
      );
    } catch (err) {
      console.error('Failed to withdraw:', err);
    } finally {
      setWithdrawingId(null);
    }
  };

  const filteredApps = filter === 'all' ? applications : applications.filter((a) => a.status === filter);

  const statusConfig: Record<string, { icon: React.ElementType; color: string; bg: string; label: string }> = {
    pending: { icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', label: 'Pending Review' },
    accepted: { icon: CheckCircle, color: 'text-green-700', bg: 'bg-green-50 border-green-200', label: 'Accepted' },
    rejected: { icon: XCircle, color: 'text-red-700', bg: 'bg-red-50 border-red-200', label: 'Not Selected' },
    withdrawn: { icon: MinusCircle, color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200', label: 'Withdrawn' },
  };

  const statusCounts = {
    all: applications.length,
    pending: applications.filter((a) => a.status === 'pending').length,
    accepted: applications.filter((a) => a.status === 'accepted').length,
    rejected: applications.filter((a) => a.status === 'rejected').length,
    withdrawn: applications.filter((a) => a.status === 'withdrawn').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard count={statusCounts.pending} label="Pending" color="bg-amber-500" />
        <SummaryCard count={statusCounts.accepted} label="Accepted" color="bg-green-500" />
        <SummaryCard count={statusCounts.rejected} label="Not Selected" color="bg-red-500" />
        <SummaryCard count={statusCounts.withdrawn} label="Withdrawn" color="bg-gray-400" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-gray-400" />
        {(['all', 'pending', 'accepted', 'rejected', 'withdrawn'] as StatusFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
              filter === f
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f === 'all' ? 'All' : f} ({statusCounts[f]})
          </button>
        ))}
      </div>

      {filteredApps.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
          <Briefcase className="h-10 w-10 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">
            {applications.length === 0
              ? 'You have not applied to any jobs yet.'
              : `No ${filter} applications found.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredApps.map((app) => {
            const config = statusConfig[app.status] || statusConfig.pending;
            const StatusIcon = config.icon;

            return (
              <div
                key={app.id}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Briefcase className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <h4 className="font-semibold text-gray-900 truncate">
                        {app.jobTitle || 'Job Position'}
                      </h4>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
                      {app.employerName && (
                        <span className="flex items-center gap-1">
                          <span className="font-medium text-gray-600">{app.employerName}</span>
                        </span>
                      )}
                      {app.jobLocation && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {app.jobLocation}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Applied {new Date(app.createdAt).toLocaleDateString()}
                      </span>
                    </div>

                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.color}`}>
                      <StatusIcon className="h-3.5 w-3.5" />
                      {config.label}
                      {app.status !== 'pending' && app.updatedAt && (
                        <span className="ml-1 opacity-75">
                          - {new Date(app.updatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {app.status === 'pending' && (
                      <button
                        onClick={() => handleWithdraw(app.id)}
                        disabled={withdrawingId === app.id}
                        className="text-xs text-gray-500 hover:text-red-600 font-medium transition-colors disabled:opacity-50"
                      >
                        {withdrawingId === app.id ? 'Withdrawing...' : 'Withdraw'}
                      </button>
                    )}
                    <a
                      href={`/jobs/${app.jobId}`}
                      className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-700 font-medium"
                    >
                      View Job
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ count, label, color }: { count: number; label: string; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
      <div className={`inline-flex items-center justify-center h-8 w-8 rounded-full ${color} text-white text-sm font-bold mb-1.5`}>
        {count}
      </div>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
    </div>
  );
}
